/**
 * My Team — saved-team model and official 2026 transfer-allowance rules.
 *
 * The user's fantasy team is ONE continuous season team: the line-up saved
 * for race N automatically becomes the starting point for race N+1. Official
 * rules (fantasy.formula1.com, 2026 season):
 *
 * - Unlimited transfers before the team's FIRST Grand Prix.
 * - After each race: 2 free transfers; each additional transfer = -10 points.
 * - 2026 change: the transfer count is the NET difference between line-ups at
 *   consecutive Grands Prix, not each swap action — reverting a swap recovers
 *   the allowance.
 * - ONE unused free transfer carries over to the next race, must be used
 *   then, and never accumulates (so at most 3 free in a race week).
 * - Wildcard/Limitless: unlimited transfers that week. After a Limitless
 *   week no free transfer carries over. (No Negative does NOT cancel
 *   transfer penalties; Boost/x3 captain changes are free.)
 *
 * All slugs here are canonical driver/constructor slugs — never DB record
 * ids, which are not stable across re-seeds.
 */

export const TRANSFER_PENALTY_POINTS = 10;
export const FREE_TRANSFERS_PER_RACE = 2;

/** Team snapshot for one race. Slot order is meaningful: with the x3 chip
 * active driver slot 0 is the x3 driver and slot 1 the x2 captain; otherwise
 * slot 0 is the x2 captain. */
export interface SavedRaceTeam {
  raceSlug: string;
  raceName: string;
  raceRound: number;
  /** 5 driver slugs in slot order (null = empty slot). */
  drivers: (string | null)[];
  /** 2 constructor slugs in slot order (null = empty slot). */
  constructors: (string | null)[];
  /** UI chip label ("x3" | "Limitless" | "No Neg") or null. */
  chip: string | null;
  budget: number;
  savedAt: number;
}

/** All saved race teams for one (user, season), keyed by race slug. */
export interface SeasonTeamVault {
  version: 1;
  races: Record<string, SavedRaceTeam>;
}

export function emptySeasonVault(): SeasonTeamVault {
  return { version: 1, races: {} };
}

/** The saved race with the highest round strictly below `round` — i.e. the
 * line-up the user carries into `round`. Limitless weeks are skipped: the
 * official game restores the pre-Limitless team after the race. */
export function findBaselineTeam(
  vault: SeasonTeamVault | null | undefined,
  round: number,
): SavedRaceTeam | null {
  if (!vault) return null;
  let best: SavedRaceTeam | null = null;
  for (const team of Object.values(vault.races)) {
    if (team.chip === "Limitless") continue;
    if (team.raceRound < round && (!best || team.raceRound > best.raceRound)) {
      best = team;
    }
  }
  return best;
}

function present(slugs: (string | null)[]): string[] {
  return slugs.filter((s): s is string => s !== null);
}

/** Net transfer count between two line-ups: baseline assets no longer in the
 * current team (drivers and constructors compared within their own pool). */
export function countNetTransfers(
  baseline: SavedRaceTeam,
  currentDrivers: (string | null)[],
  currentConstructors: (string | null)[],
): number {
  const driverSet = new Set(present(currentDrivers));
  const ctorSet = new Set(present(currentConstructors));
  let out = 0;
  for (const slug of present(baseline.drivers)) {
    if (!driverSet.has(slug)) out += 1;
  }
  for (const slug of present(baseline.constructors)) {
    if (!ctorSet.has(slug)) out += 1;
  }
  return out;
}

function isUnlimitedChip(chip: string | null): boolean {
  return chip === "Limitless";
}

export interface TransferSummary {
  /** Why transfers are free-form this week, or "counted" when the allowance applies. */
  mode: "first-race" | "chip-unlimited" | "counted";
  transfersUsed: number;
  /** 2 or 3 (with carry-over); 0 when mode is not "counted". */
  freeTransfers: number;
  /** 1 when an unused free transfer carried over from the previous race. */
  carriedOver: 0 | 1;
  extraTransfers: number;
  penaltyPoints: number;
}

/**
 * Compute the free-transfer allowance available AT `round`, walking the
 * season's saved races in round order to resolve carry-overs. Returns 0 free
 * transfers when the week is unlimited (first race / Limitless chip week —
 * callers check `mode`).
 */
export function computeTransferSummary(
  vault: SeasonTeamVault | null | undefined,
  round: number,
  currentDrivers: (string | null)[],
  currentConstructors: (string | null)[],
  currentChip: string | null,
): TransferSummary {
  const baseline = findBaselineTeam(vault, round);
  if (!baseline) {
    return {
      mode: "first-race",
      transfersUsed: 0,
      freeTransfers: 0,
      carriedOver: 0,
      extraTransfers: 0,
      penaltyPoints: 0,
    };
  }
  const transfersUsed = countNetTransfers(
    baseline,
    currentDrivers,
    currentConstructors,
  );
  if (isUnlimitedChip(currentChip)) {
    return {
      mode: "chip-unlimited",
      transfersUsed,
      freeTransfers: 0,
      carriedOver: 0,
      extraTransfers: 0,
      penaltyPoints: 0,
    };
  }

  // Walk saved races in round order to determine whether an unused free
  // transfer carries into `round`. The first saved race is the unlimited
  // week (nothing carries out of it). A Limitless week carries nothing out,
  // and does not advance the comparison baseline (the game restores the
  // pre-Limitless team after the race).
  const ordered = Object.values(vault?.races ?? {})
    .filter((t) => t.raceRound < round)
    .sort((a, b) => a.raceRound - b.raceRound);
  let carry: 0 | 1 = 0;
  let prevBaseline: SavedRaceTeam | null = null;
  for (const cur of ordered) {
    if (prevBaseline === null) {
      // First saved race: unlimited week, nothing carries out of it.
      if (!isUnlimitedChip(cur.chip)) prevBaseline = cur;
      continue;
    }
    if (isUnlimitedChip(cur.chip)) {
      carry = 0;
      continue;
    }
    const free: number = FREE_TRANSFERS_PER_RACE + carry;
    const used = countNetTransfers(prevBaseline, cur.drivers, cur.constructors);
    carry = free - used >= 1 ? 1 : 0;
    prevBaseline = cur;
  }

  const freeTransfers = FREE_TRANSFERS_PER_RACE + carry;
  const extraTransfers = Math.max(0, transfersUsed - freeTransfers);
  return {
    mode: "counted",
    transfersUsed,
    freeTransfers,
    carriedOver: carry,
    extraTransfers,
    penaltyPoints: extraTransfers * TRANSFER_PENALTY_POINTS,
  };
}
